import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';
import type { ReactNode } from 'react';

import { Logger } from '../services';

// Define the shape of the context state
interface RolesState {
  isRolesEnabled: boolean;
  isMenuPinned: boolean;
}

// Define a generic action that automatically infers from RolesState
interface Action<K extends keyof RolesState> {
  type: K;
  value: RolesState[K];
}

// Initial state
export const initialState: RolesState = {
  isRolesEnabled: false,
  isMenuPinned: false,
};

// Reducer function that infers the key and value types
const reducer = <K extends keyof RolesState>(
  state: RolesState,
  action: Action<K>
): RolesState => ({ ...state, [action.type]: action.value });

// Create the context with a default value
const RolesContext = createContext<
  | {
      rolesState: RolesState;
      updateRolesState: <K extends keyof RolesState>(
        key: K,
        value: RolesState[K]
      ) => void;
    }
  | undefined
>(undefined);

// Provider component that wraps the children
export function RolesProvider({ children }: { children: ReactNode }) {
  const [rolesState, dispatch] = useReducer(reducer, initialState);

  // Function to dispatch actions based on key and value, with no hardcoding
  const updateRolesState = useCallback(
    <K extends keyof RolesState>(key: K, value: RolesState[K]) => {
      dispatch({ type: key, value }); // No hardcoded strings!
    },
    []
  );

  // Memoize the context value to avoid unnecessary re-renders
  const value = useMemo(
    () => ({
      rolesState,
      updateRolesState,
    }),
    [rolesState, updateRolesState]
  );

  return (
    <RolesContext.Provider value={value}>{children}</RolesContext.Provider>
  );
}

// Custom hook to use the Roles context
export const useRolesContext = () => {
  const context = useContext(RolesContext);

  if (!context) {
    Logger.warn(
      'useRolesContext not within a RolesProvider, returning default values.'
    );

    return {
      rolesState: initialState,
      updateRolesState: () => {},
    };
  }

  return context;
};
